import pt from '../../locales/pt';
import { cn } from '../../utils/cn';

/**
 * `.pen` → `ds/chart/ring-progress` (`LOAo4`, 80×80) e
 * `ds/chart/ring-progress--lg` (`R8wy8`, 132×132) — mesma estrutura, duas
 * escalas, por isso um só componente com `size="sm" | "lg"`.
 *
 * Estrutura lida do master (`Get(..., {resolveVariables:true})`):
 *   track   ellipse `innerRadius` 0.806, `$track`
 *   arc     `startAngle` 90 (12h), `sweepAngle` negativo (sentido horário),
 *           `$sage-500` — `$warning-500` em excesso
 *   center  flex-col gap 2px, centrado
 *             sm: valor `$type-display-stat` + rótulo `$type-chip` 600 muted
 *             lg: valor `$type-display-hero` + rótulo `$type-label` 600 muted
 *   empty   anel de contorno 2px `$border`, «—» + «sem dados» (mantém a caixa)
 *   loading anel `$skeleton` + duas barras `rounded-ds-full`
 */

const INNER_RADIUS = 0.806;

const SIZES = {
  sm: {
    px: 80,
    valueClass: 'type-display-stat',
    labelClass: 'type-chip font-semibold',
    bars: [
      { width: 34, height: 11 },
      { width: 24, height: 7 },
    ],
    skeletonGap: 'gap-space-1',
  },
  lg: {
    px: 132,
    valueClass: 'type-display-hero',
    labelClass: 'type-label font-semibold',
    bars: [
      { width: 56, height: 18 },
      { width: 40, height: 12 },
    ],
    skeletonGap: 'gap-1.5',
  },
};

function ringGeometry(px) {
  const outer = px / 2;
  const inner = outer * INNER_RADIUS;
  return {
    stroke: outer - inner,
    radius: (outer + inner) / 2,
    circumference: 2 * Math.PI * ((outer + inner) / 2),
  };
}

export function RingProgress({
  percent = 0,
  value,
  label,
  size = 'sm',
  tone = 'default',
  state = 'default',
  emptyValue = '—',
  emptyLabel,
  ariaLabel,
  className,
  children,
}) {
  const cfg = SIZES[size] ?? SIZES.sm;
  const { stroke, radius, circumference } = ringGeometry(cfg.px);
  const box = { width: cfg.px, height: cfg.px };
  const fraction = Math.min(1, Math.max(0, Number(percent) || 0) / 100);
  const arcClass = tone === 'over' ? 'stroke-warning-500' : 'stroke-sage-500';

  if (state === 'loading') {
    return (
      <div
        className={cn('relative shrink-0', className)}
        style={box}
        aria-busy="true"
      >
        <svg
          width={cfg.px}
          height={cfg.px}
          viewBox={`0 0 ${cfg.px} ${cfg.px}`}
          aria-hidden
        >
          <circle
            cx={cfg.px / 2}
            cy={cfg.px / 2}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            className="stroke-skeleton"
          />
        </svg>
        <div
          className={cn(
            'absolute inset-0 flex flex-col items-center justify-center',
            cfg.skeletonGap,
          )}
        >
          {cfg.bars.map((bar) => (
            <span
              key={bar.width}
              className="rounded-ds-full bg-skeleton"
              style={bar}
            />
          ))}
        </div>
      </div>
    );
  }

  if (state === 'empty') {
    return (
      <div className={cn('relative shrink-0', className)} style={box}>
        <div className="absolute inset-0 rounded-ds-full border-2 border-border" />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 text-center">
          <span className={cn(cfg.valueClass, 'text-muted-foreground')}>
            {emptyValue}
          </span>
          <span className={cn(cfg.labelClass, 'text-muted-foreground')}>
            {emptyLabel || pt.common.charts.noData}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn('relative shrink-0', className)}
      style={box}
      role="img"
      aria-label={ariaLabel || `${Math.round(Number(percent) || 0)}% ${label || ''}`.trim()}
    >
      <svg
        width={cfg.px}
        height={cfg.px}
        viewBox={`0 0 ${cfg.px} ${cfg.px}`}
        aria-hidden
      >
        <circle
          cx={cfg.px / 2}
          cy={cfg.px / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          className="stroke-track"
        />
        <circle
          cx={cfg.px / 2}
          cy={cfg.px / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          className={arcClass}
          strokeDasharray={`${fraction * circumference} ${circumference}`}
          transform={`rotate(-90 ${cfg.px / 2} ${cfg.px / 2})`}
        />
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 text-center">
        {children ?? (
          <>
            <span className={cn(cfg.valueClass, 'text-foreground')}>{value}</span>
            {label ? (
              <span className={cn(cfg.labelClass, 'text-muted-foreground')}>
                {label}
              </span>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export default RingProgress;
